import {ALERT_ERROR_CLASSNAME, ALERT_INFO_CLASSNAME, API_BASE_URL, DEFAULT_ERROR_MESSAGE} from "@/constants";
import {formAlert} from "@/services/alert";

export const apiService = {
    getCsrf() {
        return document.querySelector('meta[name="csrf-token"]').getAttribute('content');
    },
    async fetchMessages(dialogHash) {
        const response = await fetch(`${API_BASE_URL}/dialogs/${dialogHash}/messages`,
            {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': this.getCsrf(),
                },
            }
        );
        if (!response.ok) {
            // eslint-disable-next-line no-use-before-define
            await prepareApiError(response);
        }
        const result = await response.json();
        return result.data;
    },
    async regenerateMessage(messageId) {
        const response = await fetch(`${API_BASE_URL}/messages/${messageId}/recreate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': this.getCsrf(),
            },
        });
        if (!response.ok) {
            // eslint-disable-next-line no-use-before-define
            await prepareApiError(response);
        }
        return response.json();
    },
    async resendUserMessage(messageId) {
        const response = await fetch(`${API_BASE_URL}/messages/${messageId}/resend`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': this.getCsrf(),
            },
        });
        if (!response.ok) {
            // eslint-disable-next-line no-use-before-define
            await prepareApiError(response);
        }
        return response.json();
    },
    async sendMessage(query, type, dialogHash, files = null, uml = null, searchDomainFilter = null, musicTitle = null, musicTags = null) {
        let formData;
        let json;
        if (files) {
            // Если есть файлы, используем FormData
            formData = new FormData();
            formData.append('q', query);
            formData.append('connect', dialogHash);
            if (uml) {
                formData.append('uml', uml);
            }
            if (searchDomainFilter) {
                formData.append('search_domain_filter', searchDomainFilter);
            }
            if (musicTitle) {
                formData.append('music_title', musicTitle);
            }
            if (musicTags) {
                formData.append('music_tags', musicTags);
            }
            // Добавляем файлы в FormData
            if (Array.isArray(files)) {
                files.forEach((file, index) => {
                    formData.append(`files[${index}]`, file);
                });
            } else {
                formData.append('files[0]', files);
            }
        } else {
            json = {q: query, connect: dialogHash};
            if (uml) {
                json.uml = uml;
            }
            if (searchDomainFilter) {
                json.search_domain_filter = searchDomainFilter;
            }
            if (musicTitle) {
                json.music_title = musicTitle;
            }
            if (musicTags) {
                json.music_tags = musicTags;
            }
        }
        const response = await fetch(`${API_BASE_URL}/messages/create`, {
            method: 'POST',
            headers: {
                ...(files ? {} : {
                    'Content-Type': 'application/json',
                }),
                'Accept': 'application/json',
                'X-CSRF-TOKEN': this.getCsrf(),
            },
            body: files ? formData : JSON.stringify(json),
        });
        if (!response.ok) {
            // eslint-disable-next-line no-use-before-define
            await prepareApiError(response);
        }
        return response.json();
    },
    async patchDialog(dialogHash, data) {
        const response = await fetch(`${API_BASE_URL}/dialogs/${dialogHash}`, {
            method: 'PATCH',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': this.getCsrf(),
            },
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            // eslint-disable-next-line no-use-before-define
            await prepareApiError(response);
        }
        return response.json();
    },
    async imageVariation(messageId, url) {
        const data = {
            link: url,
        };
        const response = await fetch(`${API_BASE_URL}/messages/${messageId}/variation`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': this.getCsrf(),
            },
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            // eslint-disable-next-line no-use-before-define
            await prepareApiError(response);
        }
        return response.json();
    },
    async removeMessage(messageId) {
        const data = {requestId: messageId};
        const response = await fetch(`${API_BASE_URL}/messages/delete`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': this.getCsrf(),
            },
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            // eslint-disable-next-line no-use-before-define
            await prepareApiError(response);
        }
        return response.json();
    },
    async updateMessageText(messageId, text) {
        const data = {text};
        const response = await fetch(`${API_BASE_URL}/messages/${messageId}`, {
            method: 'PATCH',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': this.getCsrf(),
            },
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            // eslint-disable-next-line no-use-before-define
            await prepareApiError(response);
        }
        return response.json();
    },
    async getDialog(id) {
        const response = await fetch(`${API_BASE_URL}/dialogs/${id}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': this.getCsrf(),
            },
        });
        if (!response.ok) {
            // eslint-disable-next-line no-use-before-define
            await prepareApiError(response);
        }
        return await response.json();
    },
    async getChatWidgetSettings() {
        const response = await fetch(`${API_BASE_URL}/chat-widget/settings`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': this.getCsrf(),
            },
        });
        if (!response.ok) {
            // eslint-disable-next-line no-use-before-define
            await prepareApiError(response);
        }
        return await response.json();
    },
    async getPresets() {
        const response = await fetch(`${API_BASE_URL}/presets/system`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': this.getCsrf(),
            },
        });
        if (!response.ok) {
            throw new Error('Failed to get presets');
        }
        return await response.json();
    },
    async getExamples() {
        const response = await fetch(`${API_BASE_URL}/examples`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': this.getCsrf(),
            },
        });
        if (!response.ok) {
            throw new Error('Failed to get examples');
        }
        return await response.json();
    },
    async getModels() {
        const response = await fetch(`${API_BASE_URL}/models`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': this.getCsrf(),
            },
        });
        if (!response.ok) {
            throw new Error('Failed to get models');
        }
        return await response.json();
    },
    async getUmlModes() {
        const response = await fetch(`${API_BASE_URL}/uml/modes`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': this.getCsrf(),
            },
        });
        if (!response.ok) {
            throw new Error('Failed to get uml modes');
        }
        return await response.json();
    },
    async createDialog(query, mode, model, systemPrompt = null, assistantsIds = null, webSearch = null) {
        const data = {
            q: query,
            mode,
            model,
        };
        if (systemPrompt) {
            data.system_prompt = systemPrompt
        }
        if (assistantsIds) {
            data.assistants = assistantsIds;
        }
        if (webSearch !== null) {
            data.web_search = webSearch
        }
        const response = await fetch(`${API_BASE_URL}/dialogs/create`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': this.getCsrf(),
            },
            body: JSON.stringify(data)
        });
        if (!response.ok) {
            // eslint-disable-next-line no-use-before-define
            await prepareApiError(response);
        }
        return response.json();
    }
};

async function prepareApiError(response) {
    let errorMessage = '';
    const error = new Error(errorMessage);
    const errorData = await response.json().catch(() => ({}));
    if (errorData.error?.message) {
        // контролируемый ответ от api, доступ к диалогу, неподтвержден email
        errorMessage = await formAlert(errorData.error.message, ALERT_INFO_CLASSNAME);
    } else if (errorData.message) {
        // валидация
        errorMessage = await formAlert(errorData.message, ALERT_INFO_CLASSNAME);
    } else {
        // nginx и другие ошибки
        errorMessage = await formAlert(DEFAULT_ERROR_MESSAGE, ALERT_ERROR_CLASSNAME);
    }
    // обработать 429 ошибку (много запросов от пользователя, перезагрузите страницу)
    error.message = errorMessage;
    if (errorData.error?.details?.disableAll) {
        error.disableAll = true;
    } else if (response.status === 403) {
        error.disableAll = true;
    } else if (response.status === 422) {
        error.validationError = true;
    }
    throw error;
}